import { EvaluationEvidenceError } from './evidence-error.js';
import { projectileContactMagnitude } from '../sim/predicate.js';
/**
 * Expected effective hits E of one cast: the single authority every price,
 * legality check and summary reads, so a shape cannot be priced as hitting
 * more bodies than it can reach.
 *
 * A cast aims at ONE target, the nearest enemy, at distance D. Each attack
 * unit contains that target only when its own footprint, placed by
 * `resolveAttackPose` (the runtime's placement owner), reaches the point on the
 * aim line at D. A unit that does not contain the aimed target still hits what
 * happens to stand in its footprint.
 *
 * ```text
 * e(u, a) = E_D [ N(u | target inside, others) or N(u | others) ]
 * E       = sum over units u, over applications a of u, of e(u, a)
 * ```
 *
 * For a projectile or an area, D and the other enemies come from the cast field
 * (`cast-field.ts`): when the cast fires, the empty disc around the caster, and
 * the camp behind the aimed enemy. A weapon contact keeps the homogeneous
 * Poisson model: `D` is drawn from the nearest-enemy law `P(D > t) =
 * exp(-rho * pi * t^2)` conditioned on a target within reach, and the unit
 * holds `s * N(aimed) + (1 - s) * N(occupancy)` with `s` the probability the
 * aimed target is in the footprint.
 *
 * `N(.|aimed)` and `N(.|occupancy)` are `expectedTargets` with and without the
 * guaranteed primary, so a unit with `s = 1` is exactly the single-delivery
 * authority and nothing here re-derives how one delivery reaches targets.
 *
 * Placement follows the runtime: a cast whose members are all at the identity
 * transform uses the cast's own targeting (an area lands on the target, a
 * projectile or cone is aimed at it), so every unit has `s = 1`; as soon as
 * one member is turned or shifted, EVERY member is placed relative to the
 * caster (`composite-execution.ts`, `needsPlacement`). A placed projectile
 * flies its authored heading and, like every delivery, lands 1 on the pricing basis.
 *
 * A persistent zone applies on its `deliveryApplicationSchedule`. The primary
 * stays in the zone only while straight-line enemy motion
 * (`EvalContext.encounterMotion`) keeps it inside the dilated disc; the
 * background is replaced by entrants, so each application sees the field's
 * count at the cast. A context that states no encounter motion
 * prices a zone as one application, as before.
 *
 * PROVISIONAL: real monsters chase (`context.ts` `EncounterMotionEvidence`),
 * which this straight-line model cannot express.
 */
import { discount } from '../math.js';
import { projectileLifecycle } from './projectile-lifecycle.js';
import type { EvalContext } from '../context.js';
import type { Delivery } from '../types.js';
import type { SkillApplicationV2 } from '../skill-engine/types.js';
import type { ExpandedTransform, StageEventAnchorV1 } from '../skill-engine/composite-types.js';
import { IDENTITY_CAST_POSE, resolveAttackPose } from '../sim/attack-pose.js';
import { expectedContactMagnitudeMultiplier, resolveWeaponContactEvaluation } from '../sim/weapon-contact.js';
import {
  castFieldProfile, entryDistanceM, footprintCastReachM, footprintContains, footprintRegion, memberFootprint, rotateFootprint, unionRegion, usesCastField,
  type Footprint, type FieldRegion,
} from './cast-field.js';
import { pDelivery } from './hitprob.js';
import { deliveryApplicationSchedule } from './persistence.js';
import { distinctProjectileHits, ProjectileUnionTooLarge, projectileUnionRefusal, type Corridor } from './projectile-union.js';
import { deliveryForLeaf, expectedTargets, fieldAreaModel, targetsGivenLambda } from './targeting.js';
import { appraisalEnemyRadiusM, resolveCastAim, stageAim, type CastAim } from './cast-aim.js';
export { resolveCastAim, stageAim, stageAnchor, PRIMARY_SHARE_GEOMETRY_UNAVAILABLE, type CastAim, type CastAimResult, type HitStage } from './cast-aim.js';

/** Cells of the aim-line integral; fixed so the same plan always yields the same bytes. */
const AIM_LINE_CELLS = 2048;

/**
 * P(the aimed target is in `contains`), D drawn from the nearest-enemy law
 * conditioned on a target within `reachM`. Cell weights are exact CDF
 * differences, so a footprint containing the whole line returns exactly 1.
 */
function aimLineShare(contains: (px: number, py: number) => boolean, reachM: number, density: number): number {
  const rate = Math.PI * density;
  let inside = 0;
  let total = 0;
  for (let i = 0; i < AIM_LINE_CELLS; i += 1) {
    const lo = (i * reachM) / AIM_LINE_CELLS;
    const hi = ((i + 1) * reachM) / AIM_LINE_CELLS;
    const weight = Math.exp(-rate * lo * lo) - Math.exp(-rate * hi * hi);
    total += weight;
    if (contains(0, (lo + hi) / 2)) inside += weight;
  }
  return total > 0 ? inside / total : 0;
}

interface Drift { readonly x: number; readonly y: number; readonly weight: number }

/** Whether a persistent zone's contacts are priced over its application schedule (else as one application). */
export function pricesApplicationSchedule(ctx: EvalContext): boolean {
  return enemyDrifts(ctx) !== undefined;
}

/**
 * Enemy drift through a fixed zone, from the context's own motion evidence.
 * `undefined` when the context states none (or states it unknown): a zone is
 * then one application.
 */
function enemyDrifts(ctx: EvalContext): readonly Drift[] | undefined {
  const motion = ctx.encounterMotion;
  if (motion === undefined || motion.kind === 'UNKNOWN') return undefined;
  if (motion.kind === 'STATIONARY') return [{ x: 0, y: 0, weight: 1 }];
  if (motion.kind === 'CONSTANT_VELOCITY') return [{ x: motion.enemyVelocityMps.x, y: motion.enemyVelocityMps.y, weight: 1 }];
  return motion.enemyVelocityMixture.map((branch) => ({ x: branch.velocityMps.x, y: branch.velocityMps.y, weight: branch.weight }));
}

export interface UnitHits {
  /** Σ over applications of the probability the aimed target is in the footprint. */
  readonly primaryShare: number;
  readonly expectedHits: number;
  /** The same hits when no enemy is aimed at: the footprint holds only what stands in it. */
  readonly hitsWithoutPrimary: number;
}

/**
 * The two target counts of a unit and where its footprint is, walked over the
 * unit's application schedule. `contains` tests the aim line against the whole
 * unit (one footprint, or the union of a shared unit's); `anchor` is where an
 * unplaced zone lands: on the target.
 */
function scheduledHits(
  delivery: Delivery,
  aim: CastAim,
  ctx: EvalContext,
  aimed: number,
  occupancy: number,
  contains: ((px: number, py: number) => boolean) | undefined,
  anchor: { readonly x: number; readonly y: number } | undefined,
  lambdaD = 0,
): UnitHits {
  const drifts = delivery.type === 'ground_aoe' ? enemyDrifts(ctx) : undefined;
  const offsetsS = drifts === undefined ? [0] : deliveryApplicationSchedule(delivery, ctx.tickS).offsetsS.slice();
  const pointAnchored = delivery.type === 'ground_aoe';

  let primaryShare = 0;
  let expectedHits = 0;
  let hitsWithoutPrimary = 0;
  for (const t of offsetsS) {
    let share: number;
    if (contains === undefined || (!aim.placed && !pointAnchored)) {
      share = 1;
    } else {
      const shareWithDrift = (drift: Drift): number => (aim.placed
        ? aimLineShare((px, py) => contains(px + drift.x * t, py + drift.y * t), aim.aimReachM, ctx.enemyDensity)
        // An unplaced zone lands on the target: it starts at the footprint's own centre.
        : contains(anchor!.x + drift.x * t, anchor!.y + drift.y * t) ? 1 : 0);
      share = drifts === undefined
        ? shareWithDrift({ x: 0, y: 0, weight: 1 })
        : drifts.reduce((sum, drift) => sum + drift.weight * shareWithDrift(drift), 0);
    }
    primaryShare += share;
    expectedHits += discount(t, lambdaD) * (share * aimed + (1 - share) * occupancy);
    hitsWithoutPrimary += discount(t, lambdaD) * occupancy;
  }
  return { primaryShare, expectedHits, hitsWithoutPrimary };
}

/**
 * A unit's expected hits against the cast field, walked over its application
 * schedule. `regionAt(shift)` is the unit's region with the enemies drifted by
 * `shift`; `reachM` is how far the cast reaches
 * before it fires.
 */
function fieldScheduledHits(
  delivery: Delivery,
  ctx: EvalContext,
  regionAt: (shift: { readonly x: number; readonly y: number }) => FieldRegion,
  reachM: number,
  valueOf: (targets: number) => number,
  entry = false,
  lambdaD = 0,
): UnitHits {
  const drifts = delivery.type === 'ground_aoe' ? enemyDrifts(ctx) : undefined;
  const offsetsS = drifts === undefined ? [0] : deliveryApplicationSchedule(delivery, ctx.tickS).offsetsS.slice();
  const area = fieldAreaModel(delivery);
  let primaryShare = 0;
  let expectedHits = 0;
  let hitsWithoutPrimary = 0;
  const start = castFieldProfile(regionAt({ x: 0, y: 0 }), reachM, ctx.castField, ctx.enemyDensity, entry);
  for (const t of offsetsS) {
    for (const drift of drifts ?? [{ x: 0, y: 0, weight: 1 }]) {
      // Only the primary walks out of the footprint; the others are replaced by entrants, as before.
      const drifted = castFieldProfile(regionAt({ x: drift.x * t, y: drift.y * t }), reachM, ctx.castField, ctx.enemyDensity, entry);
      start.forEach((node, i) => {
        const weight = drift.weight * node.weight;
        const inside = drifted[i]!.targetInside;
        if (inside) primaryShare += weight;
        expectedHits += discount(t, lambdaD) * weight * valueOf(targetsGivenLambda(area, node.others, inside));
        hitsWithoutPrimary += discount(t, lambdaD) * weight * valueOf(targetsGivenLambda(area, node.others, false));
      });
    }
  }
  return { primaryShare, expectedHits, hitsWithoutPrimary };
}

/** Bearings an enemy walks in from, evenly: the layout turned by each stands for the aim turned the other way. */
const ENTRY_BEARINGS = 12;

/**
 * A footprint fixed in the world, whichever way the cast aims, fires the
 * moment an enemy steps into one of the cast's fixed footprints, or into the
 * scalar reach of the cast's other members, whichever comes first
 * (`authoritative-world-core` `enemyWithinAutoReach`). The enemy comes from
 * any bearing, and stands where that bearing first meets the footprints: the
 * hits are the mean over bearings of the field with the aimed enemy there.
 * On a bearing where the scalar reach (`scalarReachM`, 0 for a cast of fixed
 * footprints alone) is the farther, the cast fires by the ordinary rule at
 * that reach and the enemy is not guaranteed to stand in a fixed footprint.
 * A footprint too small for any of the even bearings to meet is entered along
 * the bearing through its own centre. `undefined` when no footprint can be entered.
 */
function entryHits(
  gate: readonly Footprint[],
  scalarReachM: number,
  hitsAt: (turnRad: number, reachM: number, entry: boolean) => UnitHits,
): UnitHits | undefined {
  let primaryShare = 0;
  let expectedHits = 0;
  let hitsWithoutPrimary = 0;
  let bearings = 0;
  const even = Array.from({ length: ENTRY_BEARINGS }, (_unused, i) => (2 * Math.PI * i) / ENTRY_BEARINGS);
  const entryAt = (turnRad: number): number => entryDistanceM(gate.map((shape) => rotateFootprint(shape, turnRad)));
  const turns = scalarReachM > 0 || even.some((turnRad) => entryAt(turnRad) > 0)
    ? even
    : gate.map((shape) => Math.PI / 2 - Math.atan2(shape.y, shape.x));
  for (const turnRad of turns) {
    const entryM = entryAt(turnRad);
    const reachM = Math.max(entryM, scalarReachM);
    if (!(reachM > 0)) continue;
    const hits = hitsAt(turnRad, reachM, entryM > scalarReachM);
    primaryShare += hits.primaryShare;
    expectedHits += hits.expectedHits;
    hitsWithoutPrimary += hits.hitsWithoutPrimary;
    bearings += 1;
  }
  return bearings === 0 ? undefined : { primaryShare: primaryShare / bearings, expectedHits: expectedHits / bearings, hitsWithoutPrimary: hitsWithoutPrimary / bearings };
}

/** The reach the distance law is drawn up to: the footprint's own, no farther than the stage fires. */
const drawnReachM = (reachM: number, aim: CastAim): number => Math.min(reachM, aim.fireReachM ?? Number.POSITIVE_INFINITY);

/** Whether an unplaced footprint stands on the aimed enemy: whatever an event releases at its point, and an area that is not a caster circle. */
const landsOnTarget = (delivery: Delivery, aim: CastAim): boolean => !aim.placed && (aim.atEventPoint === true || (delivery.type === 'ground_aoe' && delivery.center !== 'CASTER'));

/**
 * Expected effective hits of ONE attack unit that has a single footprint.
 *
 * `valueOf` maps a target count to what the caller prices it at (identity for
 * appraisal; landing and the weapon-contact magnitude mix for numeric
 * allocation), applied to each of the two target counts before they mix.
 */
export function unitExpectedHits(
  delivery: Delivery,
  application: SkillApplicationV2 | undefined,
  transform: ExpandedTransform,
  aim: CastAim,
  ctx: EvalContext,
  valueOf: (targets: number) => number = (targets) => targets,
  /** Zero counts runtime contacts; pricing supplies the same rate as the damage walk. */
  lambdaD = 0,
): UnitHits {
  const enemyRadiusM = appraisalEnemyRadiusM(ctx);
  if (delivery.type === 'projectile' && ctx.projectileTargets !== undefined) {
    const projection = projectileLifecycle(delivery, ctx, aim.fireReachM, aim.atEventPoint, transform);
    if (projection === undefined) throw new EvaluationEvidenceError('COMPOSITE_STAGE_EVENT_COUNT_MODEL_UNAVAILABLE:declared projectile field has no host projection');
    const contacts = valueOf(projection.reduce((sum, node) => sum + node.weight * node.batches.flatMap((batch) => batch.targets).reduce((n, _target, ordinal) => n + projectileContactMagnitude('damage', delivery.pierceFalloff?.rate ?? 1, ordinal), 0), 0));
    return { expectedHits: contacts, hitsWithoutPrimary: 0, primaryShare: 1 };
  }
  if (usesCastField(delivery, application)) {
    const footprint = memberFootprint(delivery, application, transform, enemyRadiusM);
    if (footprint !== undefined) {
      // Only a cast that lays footprints in the world waits for an enemy to step into one; a stage an event releases is not that cast's trigger.
      if (transform.worldFixed === true && aim.worldFixed !== undefined) {
        const entered = entryHits(aim.worldFixed, aim.castReachM, (turnRad, reachM, entry) => fieldScheduledHits(
          delivery, ctx, (shift) => footprintRegion(rotateFootprint(footprint, turnRad), 'CASTER', shift), reachM, valueOf, entry, lambdaD));
        if (entered !== undefined) return entered;
      }
      const onTarget = landsOnTarget(delivery, aim);
      const reachM = drawnReachM(onTarget ? ctx.castField.engageRangeM : aim.placed ? aim.castReachM : footprintCastReachM(footprint, enemyRadiusM), aim);
      return fieldScheduledHits(delivery, ctx, (shift) => footprintRegion(footprint, onTarget ? 'TARGET' : 'CASTER', shift), reachM, valueOf, false, lambdaD);
    }
  }
  const aimed = valueOf(expectedTargets(delivery, ctx, true, application));
  const occupancy = valueOf(expectedTargets(delivery, ctx, false, application));
  const footprint = aim.placed || delivery.type === 'ground_aoe' ? memberFootprint(delivery, application, transform, enemyRadiusM) : undefined;
  return scheduledHits(delivery, aim, ctx, aimed, occupancy,
    footprint === undefined ? undefined : (px, py) => footprintContains(footprint, px, py), footprint, lambdaD);
}

type AreaFootprint = Footprint;

/** Grid resolution of the union area, per axis; fixed so the same plan yields the same bytes. */
const UNION_GRID_SAMPLES_PER_AXIS = 768;

export function unionAreaM2(shapes: readonly AreaFootprint[]): number {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const shape of shapes) {
    const reach = shape.kind === 'DISC' ? shape.radiusM : shape.kind === 'SECTOR' ? shape.reachM : Math.hypot(shape.lengthM, shape.halfWidthM);
    minX = Math.min(minX, shape.x - reach);
    minY = Math.min(minY, shape.y - reach);
    maxX = Math.max(maxX, shape.x + reach);
    maxY = Math.max(maxY, shape.y + reach);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  if (!(width > 0) || !(height > 0)) return 0;
  const n = UNION_GRID_SAMPLES_PER_AXIS;
  const cellArea = (width / n) * (height / n);
  let covered = 0;
  for (let i = 0; i < n; i += 1) {
    const px = minX + ((i + 0.5) * width) / n;
    for (let j = 0; j < n; j += 1) {
      const py = minY + ((j + 0.5) * height) / n;
      if (shapes.some((shape) => footprintContains(shape, px, py))) covered += 1;
    }
  }
  return covered * cellArea;
}

/** Two footprints are the same region when they sit at the same point and, for a direction-sensitive shape, face the same way. */
function sameRegion(a: AreaFootprint, b: AreaFootprint): boolean {
  if (a.kind !== b.kind || a.x !== b.x || a.y !== b.y) return false;
  return a.kind === 'DISC' || a.facingRad === (b as Exclude<AreaFootprint, { kind: 'DISC' }>).facingRad;
}

/** A shared attack's deduplicated coverage is established for area footprints only: a projectile's corridor has none. */
export const unionCoverageModelled = (deliveryType: Delivery['type']): boolean => deliveryType === 'ground_aoe' || deliveryType === 'cone';

export type UnionHitsResult = ({ readonly ok: true } & UnitHits) | { readonly ok: false; readonly code: string; readonly message: string };

export const UNION_COVERAGE_MODEL_UNAVAILABLE = 'COMPOSITE_UNION_COVERAGE_MODEL_UNAVAILABLE';

/**
 * Expected effective hits of ONE shared-union attack: a target standing in
 * several footprints is hit once, so the unit holds the deduplicated union
 * area (uncapped area semantics: the aimed primary plus a Poisson field over
 * it). Its primary share is the probability the aimed target is in ANY
 * footprint, and a persistent union applies on the same schedule as a single
 * zone, with the primary surviving only while it stays inside the union.
 *
 * `independentAttacks` is for attacks that are separate in the runtime and are
 * counted once per enemy only by an event (`per: TARGET`): a projectile stops
 * after its pierce count, so the enemies of such a set are the first `cap` of
 * each corridor, not everyone in the union (`projectile-union.ts`). A shared
 * union of projectiles is not that: the runtime resolves it as one launch.
 */
export function unionExpectedHits(
  delivery: Delivery,
  application: SkillApplicationV2 | undefined,
  transforms: readonly ExpandedTransform[],
  aim: CastAim,
  ctx: EvalContext,
  valueOf: (targets: number) => number = (targets) => targets,
  independentAttacks = false,
  lambdaD = 0,
): UnionHitsResult {
  const footprints: AreaFootprint[] = [];
  const projectileCounted = independentAttacks && delivery.type === 'projectile' && delivery.pierceFalloff === undefined && usesCastField(delivery, application);
  for (const transform of transforms) {
    // The same per-member footprint independent pricing uses: a spear thrust is a corridor here too.
    // A projectile's corridor has a deduplicated count only as independent attacks.
    const footprint = memberFootprint(delivery, application, transform, appraisalEnemyRadiusM(ctx));
    if (!(unionCoverageModelled(delivery.type) || projectileCounted) || footprint === undefined) {
      return {
        ok: false,
        code: UNION_COVERAGE_MODEL_UNAVAILABLE,
        message:
          `a shared attack spans ${transforms.length} ${delivery.type} footprints, ` +
          'and no established model computes the deduplicated coverage of that delivery; ' +
          'summing the members would price one hit several times',
      };
    }
    footprints.push(footprint);
  }
  const distinct = footprints.filter((shape, index) => footprints.findIndex((other) => sameRegion(other, shape)) === index);
  // Coincident footprints are literally one region: the single-footprint answer is exact.
  if (distinct.length === 1) return { ok: true, ...unitExpectedHits(delivery, application, transforms[0]!, aim, ctx, valueOf, lambdaD) };

  if (delivery.type === 'projectile') return projectileDistinctHits(delivery, distinct as Corridor[], transforms.every((transform) => transform.worldFixed === true), aim, ctx, valueOf);

  if (usesCastField(delivery, application)) {
    if (aim.worldFixed !== undefined && transforms.every((transform) => transform.worldFixed === true)) {
      const entered = entryHits(aim.worldFixed, aim.castReachM, (turnRad, reachM, entry) => fieldScheduledHits(
        delivery, ctx, (shift) => unionRegion(distinct.map((shape) => rotateFootprint(shape, turnRad)), 'CASTER', shift), reachM, valueOf, entry, lambdaD));
      if (entered !== undefined) return { ok: true, ...entered };
    }
    const onTarget = landsOnTarget(delivery, aim);
    const reachM = drawnReachM(onTarget ? ctx.castField.engageRangeM : aim.placed ? aim.castReachM : distinct.reduce((max, shape) => Math.max(max, footprintCastReachM(shape, appraisalEnemyRadiusM(ctx))), -Infinity), aim);
    return { ok: true, ...fieldScheduledHits(delivery, ctx, (shift) => unionRegion(distinct, onTarget ? 'TARGET' : 'CASTER', shift), reachM, valueOf, false, lambdaD) };
  }
  const lambda = ctx.enemyDensity * unionAreaM2(distinct);
  return {
    ok: true,
    ...scheduledHits(delivery, aim, ctx, valueOf(1 + lambda), valueOf(lambda), (px, py) => distinct.some((shape) => footprintContains(shape, px, py)), distinct[0], lambdaD),
  };
}

/**
 * The distinct enemies of independent projectiles, mixed over the cast field's distance law exactly as an
 * area's are (a cast fixed in the world fires as an enemy steps in; a stage released at an event stands on
 * the enemy). `valueOf` is applied to the expected count, which is exact for the linear value a projectile has.
 */
function projectileDistinctHits(
  delivery: Extract<Delivery, { readonly type: 'projectile' }>,
  corridors: readonly Corridor[],
  worldFixed: boolean,
  aim: CastAim,
  ctx: EvalContext,
  valueOf: (targets: number) => number,
): UnionHitsResult {
  const cap = delivery.maxPierce ?? 1;
  const refusal = projectileUnionRefusal(corridors.length, cap);
  if (refusal !== undefined) return { ok: false, ...refusal };
  const hitsAt = (shapes: readonly Corridor[], frame: FieldRegion['frame'], reachM: number, entry: boolean): UnitHits => {
    const hits = distinctProjectileHits(shapes, cap, frame, reachM, entry, ctx.castField, ctx.enemyDensity);
    return { primaryShare: hits.primaryShare, expectedHits: valueOf(hits.distinct), hitsWithoutPrimary: valueOf(hits.distinctWithoutPrimary) };
  };
  try {
    if (aim.worldFixed !== undefined && worldFixed) {
      const entered = entryHits(aim.worldFixed, aim.castReachM, (turnRad, reachM, entry) => hitsAt(corridors.map((shape) => rotateFootprint(shape, turnRad) as Corridor), 'CASTER', reachM, entry));
      if (entered !== undefined) return { ok: true, ...entered };
    }
    const onTarget = landsOnTarget(delivery, aim);
    const reachM = drawnReachM(onTarget ? ctx.castField.engageRangeM : aim.placed ? aim.castReachM : Math.max(...corridors.map((shape) => footprintCastReachM(shape, appraisalEnemyRadiusM(ctx)))), aim);
    return { ok: true, ...hitsAt(corridors, onTarget ? 'TARGET' : 'CASTER', reachM, false) };
  } catch (error) {
    if (error instanceof ProjectileUnionTooLarge) return { ok: false, ...error.refusal };
    throw error;
  }
}

/**
 * How a unit's expected hits compare with what the legacy pricing walk
 * (`eval/effects.ts`) already charges one effect clone of its delivery:
 * `expectedTargets(delivery) × applications`. The assembled pricing
 * representative scales the unit's damage by this ratio so budget, final
 * legality and appraisal price the unit at the same E as numeric allocation.
 * Exactly 1 for an unplaced instant unit; a context with no encounter motion
 * leaves a zone on the walk's own schedule. `regionMagnitude` is whether the
 * unit's damage leaves deal the weapon contact's region magnitudes
 * (`effectsInheritWeaponRegionMagnitude`); both counts then carry the mix.
 */
export function unitPricingRatio(
  delivery: Delivery,
  application: SkillApplicationV2 | undefined,
  transforms: readonly ExpandedTransform[],
  aim: CastAim,
  ctx: EvalContext,
  regionMagnitude: boolean,
  lambdaD = 0,
): number | { readonly code: string; readonly message: string } {
  const offsetsS = deliveryApplicationSchedule(delivery, ctx.tickS).offsetsS;
  const walkApplications = offsetsS.reduce((sum, offsetS) => sum + discount(offsetS, lambdaD), 0);
  const contact = regionContactEvaluation(application, regionMagnitude, ctx);
  const valueOf = (n: number): number => n * (contact === undefined ? 1 : expectedContactMagnitudeMultiplier(contact, n));
  const walked = valueOf(expectedTargets(delivery, ctx, true, application)) * walkApplications;
  // Mix the aimed and occupancy magnitudes before comparing with the walk, just as allocation does.
  const hits = transforms.length > 1
    ? unionExpectedHits(delivery, application, transforms, aim, ctx, valueOf, false, lambdaD)
    : { ok: true as const, ...unitExpectedHits(delivery, application, transforms[0] ?? IDENTITY_TRANSFORM, aim, ctx, valueOf, lambdaD) };
  if (!hits.ok) return hits;
  // Without stated encounter motion a zone is one application here; the walk's own schedule stands.
  const unitHits = delivery.type === 'ground_aoe' && enemyDrifts(ctx) === undefined ? hits.expectedHits * walkApplications : hits.expectedHits;
  // An area with no scheduled application delivers nothing on either path.
  if (walkApplications === 0) return 0;
  const ratio = unitHits / walked;
  return Math.abs(ratio - 1) < 1e-12 ? 1 : ratio;
}

/**
 * `unitPricingRatio` for an attack a hit starts (a blast, a zone) rather than one the cast aims: it is laid on the enemy the
 * source attack struck, unplaced, and numeric allocation counts it with the same model (`resolveCoverageFactorForApplication`).
 * A persistent zone's ratio is its field hits over the walk's `expectedTargets x applications`; an instant area's is 1.
 */
export function nestedAttackPricingRatio(
  delivery: Delivery,
  application: SkillApplicationV2 | undefined,
  ctx: EvalContext,
  regionMagnitude: boolean,
  lambdaD = 0,
): number | { readonly code: string; readonly message: string } {
  return unitPricingRatio(delivery, application, [IDENTITY_TRANSFORM], { placed: false, aimReachM: 0, castReachM: 0 }, ctx, regionMagnitude, lambdaD);
}

/** The weapon contact whose region magnitudes an attack's hits carry; none for another owner or for damage that deals its full amount. */
function regionContactEvaluation(application: SkillApplicationV2 | undefined, regionMagnitude: boolean, ctx: EvalContext) {
  return regionMagnitude && application?.geometry.owner === 'WEAPON_CONTACT'
    ? resolveWeaponContactEvaluation(application, appraisalEnemyRadiusM(ctx)) : undefined;
}

export interface PricedStage {
  readonly stageId: string;
  readonly delivery: Delivery;
  readonly application?: SkillApplicationV2;
  /**
   * Set when the stage's damage deals its full amount on every contact rather than the weapon contact's region magnitudes
   * (`effectsInheritWeaponRegionMagnitude` is false): its hits then carry no region mix.
   */
  readonly fullMagnitude?: true;
  /** The footprints of each attack unit, one entry per unit (a shared union has several, priced as one unit over their union). */
  readonly units: readonly (readonly ExpandedTransform[])[];
  /** Where an event releases the stage (`HitStage.anchor`). */
  readonly anchor?: StageEventAnchorV1;
  /** The units' attacks are separate in the runtime and only an event counts their enemies once (`unionExpectedHits`). */
  readonly independentAttacks?: true;
  /** The farthest ahead of the caster the runtime fires the stage (`CastAim.fireReachM`); absent where the footprint's own reach is it. */
  readonly fireReachM?: number;
}

export interface PricedStageHits {
  /** Effective hits as priced: landing chance and weapon-contact magnitude mix included. */
  readonly expectedHits: number;
  /** `expectedHits` for a release that lands on no aimed enemy. */
  readonly hitsWithoutPrimary: number;
  readonly primaryShare: number;
  /** Expected contacts (unit x enemy) with the magnitude mix left out: what an event counts. */
  readonly contacts: number;
  /** `contacts` of each attack unit, in `units` order. */
  readonly unitContacts: readonly number[];
  /** `unitContacts` for a release that lands on no aimed enemy. */
  readonly unitContactsWithoutPrimary: readonly number[];
}

export type PricedCastHits =
  | { readonly ok: true; readonly byStageId: ReadonlyMap<string, PricedStageHits> }
  | { readonly ok: false; readonly code: string; readonly message: string };

/**
 * Expected effective hits per stage as a cast is PRICED: each unit's two
 * target counts are scaled by the delivery's landing chance and the
 * weapon-contact magnitude mix, exactly as `resolveCoverageFactorForApplication`
 * scales a single attack's. Numeric allocation prices with this and a stored
 * skill's summary reports it, so both read one number.
 */
export function pricedCastExpectedHits(stages: readonly PricedStage[], ctx: EvalContext, lambdaD = 0): PricedCastHits {
  const cast = resolveCastAim(stages.map((stage) => ({ delivery: stage.delivery, application: stage.application, transforms: stage.units.flat(), anchor: stage.anchor })), ctx);
  if (!cast.ok) return cast;
  const byStageId = new Map<string, PricedStageHits>();
  for (const stage of stages) {
    const aim = { ...cast, aim: stage.fireReachM === undefined ? stageAim(cast.aim, stage) : { ...stageAim(cast.aim, stage), fireReachM: stage.fireReachM } };
    const landing = pDelivery(stage.delivery, ctx);
    const contact = regionContactEvaluation(stage.application, stage.fullMagnitude !== true, ctx);
    const valueOf = (n: number): number => n * landing * (contact === undefined ? 1 : expectedContactMagnitudeMultiplier(contact, n));
    const countOf = (n: number): number => n * landing;
    const hitsOf = (transforms: readonly ExpandedTransform[], value: (targets: number) => number, rate = 0, delivery = stage.delivery): UnionHitsResult => (transforms.length > 1
      ? unionExpectedHits(delivery, stage.application, transforms, aim.aim, ctx, value, stage.independentAttacks === true, rate)
      : { ok: true as const, ...unitExpectedHits(delivery, stage.application, transforms[0] ?? IDENTITY_TRANSFORM, aim.aim, ctx, value, rate) });
    let expectedHits = 0;
    let hitsWithoutPrimary = 0;
    let primaryShare = 0;
    const unitContacts: number[] = [];
    const unitContactsWithoutPrimary: number[] = [];
    for (const transforms of stage.units) {
      const unit = hitsOf(transforms, valueOf, lambdaD);
      if (!unit.ok) return unit;
      expectedHits += unit.expectedHits;
      hitsWithoutPrimary += unit.hitsWithoutPrimary;
      primaryShare += unit.primaryShare;
      // Events count physical contacts, without damage magnitude or time discount.
      const counted = contact === undefined && lambdaD === 0 && deliveryForLeaf(stage.delivery, 'status') === stage.delivery ? unit : hitsOf(transforms, countOf, 0, deliveryForLeaf(stage.delivery, 'status'));
      if (!counted.ok) return counted;
      const contactField = stage.delivery.type === 'projectile' && ctx.projectileTargets !== undefined
        ? projectileLifecycle(stage.delivery, ctx, aim.aim.fireReachM, aim.aim.atEventPoint, transforms[0]) : undefined;
      unitContacts.push(contactField === undefined ? counted.expectedHits
        : landing * contactField.reduce((sum, node) => sum + node.weight * node.batches.reduce((total, batch) => total + batch.targets.length, 0), 0));
      unitContactsWithoutPrimary.push(counted.hitsWithoutPrimary);
    }
    byStageId.set(stage.stageId, { expectedHits, hitsWithoutPrimary, primaryShare, contacts: unitContacts.reduce((sum, hits) => sum + hits, 0), unitContacts, unitContactsWithoutPrimary });
  }
  return { ok: true, byStageId };
}

const IDENTITY_TRANSFORM: ExpandedTransform = Object.freeze({ x: 0, y: 0, directionRad: 0 });
